import React, { useEffect, useState } from 'react';

// helpers
import { isSafari } from 'helpers/browserHelpers';
import {
  AssertionForLoginModel,
  GetChallengeForLoginResponseModel,
} from 'typings/profile/fido';
import {
  preformatChallengeForLogin,
  publicKeyCredentialToJSON,
} from 'helpers/fidoHelpers';

// components
import LoadingWrapper from '../../../../../../WrapperComponents/LoadingWrapper';
import ErrorWithRetryButton from './ErrorWithRetryButton';
import ContentWithUseKeyButton from './ContentWithUseKeyButton';

interface IProps {
  challenge: GetChallengeForLoginResponseModel | null;
  loadingChallenge: boolean;
  onAssert: () => void;
  submitAssertion: (assertion: AssertionForLoginModel) => Promise<any>;
}

const Content = ({
  challenge,
  loadingChallenge,
  onAssert,
  submitAssertion,
}: IProps) => {
  const isSafariBrowser = isSafari();
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Safari browser is not allowing WebAuthn usage without user interaction
    // To use WebAuthn user will have to click on special button (by that trigger onClick event where we can run ceremony)
    if (challenge && !isSafariBrowser) {
      runFidoCeremony();
    }
  }, [challenge, isSafariBrowser]);

  async function runFidoCeremony() {
    if (challenge) {
      setIsError(false);
      setIsLoading(true);
      const publicKey = preformatChallengeForLogin(
        challenge.assertionChallenge,
      );

      await navigator.credentials
        .get({ publicKey })
        .then(async (assertion) => {
          if (assertion) {
            const formattedAssertion = publicKeyCredentialToJSON(
              assertion,
            ) as AssertionForLoginModel;
            await submitAssertion(formattedAssertion);
            onAssert();
          }
        })
        .catch(() => {
          setIsError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  return (
    <LoadingWrapper loading={isLoading || loadingChallenge}>
      {isSafariBrowser && !isError ? (
        <ContentWithUseKeyButton onTryClick={runFidoCeremony} />
      ) : (
        <ErrorWithRetryButton onRetryClick={runFidoCeremony} />
      )}
    </LoadingWrapper>
  );
};

export default Content;
